import React, { useRef, useCallback, useEffect, useState } from 'react'
import type {
    ApplyV2SettingsSectionName,
    ApplyV2TempCourseFile,
    ApplyV2TempProfileCvData,
    GetApplyV2SettingsResponse,
    ReviewApplySection,
} from '@core/app/types/applyV2Type'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import ApplySkeleton from '@core/components/ApplyDialogV2/ApplySkeleton'
import {
    useDeleteApplyV2AttachmentsMutation,
    usePostApplyV2ApplicationTextMutation,
    usePostApplyV2AttachmentsMutation,
    usePostApplyV2SendApplicationMutation,
} from '@core/app/api/applyJobV2Api'
import CustomRadio from '@core/components/ApplyDialogV2/CustomRadio'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import ApplyV2ReviewApplyStepItem from '@core/components/ApplyDialogV2/ApplyV2ReviewApplyStep/ApplyV2ReviewApplyStepItem'
import FileUploaderV2 from '@core/components/ApplyDialogV2/FileUploaderV2'
import FileUploaderV2List from '@core/components/ApplyDialogV2/FileUploaderV2List'
import ApplyV2Textarea from '@core/components/ApplyDialogV2/ApplyV2Textarea'
import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import ApplyV2Accordion from '@core/components/ApplyDialogV2/ApplyV2Accordion'
import './ApplyV2ReviewApplyStep.scss'

const baseClass = 'ApplyV2ReviewApplyStep'

interface Props {
    settingsData: GetApplyV2SettingsResponse | null | undefined
    navItems: {
        name: ApplyV2SettingsSectionName
        visible: boolean
        required: boolean
        order: number
    }[]
}

function ApplyV2ReviewApplyStep({ settingsData, navItems }: Props) {
    const { getTitle } = useApplyV2NavTranslations()
    const {
        postProfileData,
        postProfileError,
        isPostProfileLoading,
        isPostProfileSuccess,
        isSettingsLoading,
        isSettingsFetching,
        isFinalStep,
        setIsFinalStep,
        isPostApplied,
        onCloseApply,
    } = useApplyV2Context()

    const [files, setFiles] = useState<ApplyV2TempProfileCvData[]>([])
    const [coverText, setCoverText] = useState<string>('')
    const [coverTextError, setCoverTextError] = useState<string>('')
    const [radioVal, setRadioVal] = useState<number | string>(1)
    const debounceRef = useRef<NodeJS.Timeout | null>(null)
    const [
        postApplicationTextRequest,
        {
            data: postApplicationTextData,
            isLoading: isPostApplicationTextLoading,
            isSuccess: isPostApplicationTextSuccess,
            isError: isPostApplicationTextError,
            error: postApplicationTextError,
        },
    ] = usePostApplyV2ApplicationTextMutation()
    const [
        postAttachmentRequest,
        { data: postAttachmentData, isLoading: isPostAttachmentLoading, isError: isPostAttachmentError },
    ] = usePostApplyV2AttachmentsMutation()
    const [
        deleteAttachmentRequest,
        { data: deleteAttachmentData, isLoading: isDeleteAttachmentLoading, isError: isDeleteAttachmentError },
    ] = useDeleteApplyV2AttachmentsMutation()
    const [postSendApplicationRequest, { data: postSendData, isLoading: isPostSendLoading, isError: isPostSendError }] =
        usePostApplyV2SendApplicationMutation()

    useEffect(() => {
        if (isPostSendLoading) {
            setIsFinalStep(true)
        }
    }, [isPostSendLoading])

    useEffect(() => {
        setCoverText(postProfileData?.data?.applicationText || '')
    }, [postProfileData?.data?.applicationText])

    useEffect(() => {
        setFiles(postProfileData?.data?.attachments || [])
    }, [postProfileData?.data?.attachments])

    const coverTextCount = React.useMemo(() => {
        return coverText.length
    }, [coverText])

    const isLoading = isPostProfileLoading || isSettingsLoading || isSettingsFetching

    const attachments = postProfileData?.data?.attachments || []

    const reviewApply = settingsData?.steps?.reviewApply
    const coverTextLimit = reviewApply?.fields.applicationText.options.maxLength || 1500
    const coverTextMin = reviewApply?.fields.applicationText.options.minLength || 100
    const coverLetterRequired = reviewApply?.fields.applicationText.required || false
    const attachmentMaxFiles = reviewApply?.fields.attachments.options.maxFiles || 1
    const attachmentDescription = reviewApply?.fields.attachments.options.descriptionText || ''

    const isValidForm = React.useMemo(() => {
        const isApplicationTextRequired = reviewApply?.fields.applicationText.required ?? false
        const isAttachmentsRequired = reviewApply?.fields.attachments.required ?? false
        const isAnonymousReasonRequired = reviewApply?.fields.anonymousReason.required ?? false

        const isValidCoverText =
            !isApplicationTextRequired || (coverText.length >= coverTextMin && coverText.length <= coverTextLimit)

        const isValidAttachments = !isAttachmentsRequired || (files.length > 0 && files.length <= attachmentMaxFiles)

        return isValidCoverText && isValidAttachments && !isPostApplicationTextError
    }, [
        reviewApply?.fields.applicationText.required,
        reviewApply?.fields.attachments.required,
        reviewApply?.fields.anonymousReason.required,
        coverText,
        coverTextMin,
        coverTextLimit,
        files.length,
        attachmentMaxFiles,
        isPostApplicationTextError,
    ])

    const convertFileToBase64Payload = (file: File): Promise<{ name: string; base64: string; ext: string }> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()

            reader.onload = () => {
                const base64 = (reader.result as string).split(',')[1]
                const ext = file.name.split('.').pop()?.toLowerCase() || ''
                resolve({ name: file.name, base64, ext })
            }

            reader.onerror = (err) => {
                console.log('Error reading file:', err)
                reject(err)
            }

            reader.readAsDataURL(file)
        })
    }

    const handlePostSendApplication = (retry: boolean | undefined = false) => {
        if (!postProfileData?.data?.uid) {
            console.warn('postProfileData is not available')
            return
        }

        if (!retry) {
            setIsFinalStep(true)
        }

        postSendApplicationRequest({
            uid: postProfileData.data.uid,
        })
            .unwrap()
            .then((response) => {
                //set window.location
            })
            .catch((error) => {
                console.error('Error sending application:', error)
            })
    }

    const onSetAttachment = (vals: File[]) => {
        const uid = postProfileData?.data?.uid
        // setFiles(vals)
        // setFiles((prev) => {
        //     const existingFiles = prev.map((f) => f.name)
        //     const newFiles = vals.filter((f) => !existingFiles.includes(f.name))
        //     return [...prev, ...newFiles]
        // })

        if (!uid) return
        vals.forEach(async (file) => {
            postAttachmentRequest({
                uid,
                file: await convertFileToBase64Payload(file),
            })
                .unwrap()
                .then((response) => {
                    const uploadedFile = response.data.file
                    setFiles((prev) => {
                        const existingFiles = prev.map((f) => f.uid)
                        return [...prev, uploadedFile]
                    })
                })
                .catch((error: any) => {
                    console.error('Error uploading attachment:', error)
                })
        })
    }

    const onDeleteAttachment = (file: File | ApplyV2TempCourseFile) => {
        const uid = postProfileData?.data?.uid
        if (!uid) return
        setFiles((prev) => prev.filter((f) => f.name !== file.name))

        if ('uid' in file && file.uid) {
            deleteAttachmentRequest({
                uid,
                fileUid: file.uid,
            })
                .unwrap()
                .then((response) => {
                    console.log('Attachment deleted successfully:', response)
                })
                .catch((error: any) => {
                    console.error('Error deleting attachment:', error)
                })
        }
    }

    const handlePostApplicationText = useCallback(
        (val: string) => {
            setCoverTextError('')
            setCoverText(val)

            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
            }

            debounceRef.current = setTimeout(async () => {
                if (!postProfileData?.data?.uid) {
                    console.warn('postProfileData is not available')
                    return
                }
                try {
                    await postApplicationTextRequest({
                        uid: postProfileData?.data?.uid,
                        applicationText: val,
                    }).unwrap()
                } catch (error: any) {
                    const errorMessages = error?.data?.errors || []

                    const errors = errorMessages
                        .filter((err: any) => err.field === 'applicationText')
                        .map((err: any) => err.msg)
                    setCoverTextError(errors.join(', '))
                }
            }, 500)
        },
        [postProfileData?.data?.uid]
    )

    if (isPostSendLoading && isFinalStep) {
        return (
            <div className={`${baseClass}__final`}>
                <div className={`${baseClass}__final--icon--loader ${baseClass}__final--icon`}>
                    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M28 0C29.2768 0 30.3119 1.03508 30.3119 2.31193V7.44954C30.3119 8.72638 29.2768 9.76147 28 9.76147C26.7232 9.76147 25.6881 8.72638 25.6881 7.44954V2.31193C25.6881 1.03508 26.7232 0 28 0ZM14 3.75196C15.1057 3.11352 16.5197 3.49237 17.1581 4.59814L19.7269 9.04731C20.3654 10.1531 19.9865 11.567 18.8808 12.2055C17.775 12.8439 16.361 12.4651 15.7226 11.3593L13.1538 6.91012C12.5154 5.80435 12.8942 4.39039 14 3.75196ZM42 3.75196C43.1058 4.39039 43.4846 5.80435 42.8462 6.91012L40.2774 11.3593C39.639 12.4651 38.225 12.8439 37.1192 12.2055C36.0135 11.567 35.6346 10.1531 36.2731 9.04731L38.8419 4.59814C39.4803 3.49237 40.8943 3.11352 42 3.75196ZM3.75196 14C4.39039 12.8942 5.80435 12.5154 6.91012 13.1538L11.3593 15.7226C12.4651 16.361 12.8439 17.775 12.2055 18.8808C11.567 19.9865 10.1531 20.3654 9.04731 19.7269L4.59814 17.1581C3.49237 16.5197 3.11352 15.1057 3.75196 14ZM52.248 14C52.8865 15.1057 52.5076 16.5197 51.4019 17.1581L46.9527 19.7269C45.8469 20.3654 44.433 19.9865 43.7945 18.8808C43.1561 17.775 43.5349 16.361 44.6407 15.7226L49.0899 13.1538C50.1956 12.5154 51.6096 12.8942 52.248 14ZM0 28C0 26.7232 1.03508 25.6881 2.31193 25.6881H7.44954C8.72638 25.6881 9.76147 26.7232 9.76147 28C9.76147 29.2768 8.72638 30.3119 7.44954 30.3119H2.31193C1.03508 30.3119 0 29.2768 0 28ZM46.2385 28C46.2385 26.7232 47.2736 25.6881 48.5505 25.6881H53.6881C54.9649 25.6881 56 26.7232 56 28C56 29.2768 54.9649 30.3119 53.6881 30.3119H48.5505C47.2736 30.3119 46.2385 29.2768 46.2385 28ZM12.2055 37.1192C12.8439 38.225 12.4651 39.639 11.3593 40.2774L6.91012 42.8462C5.80435 43.4846 4.39039 43.1058 3.75196 42C3.11352 40.8943 3.49237 39.4803 4.59814 38.8419L9.04731 36.2731C10.1531 35.6346 11.567 36.0135 12.2055 37.1192ZM43.7945 37.1192C44.433 36.0135 45.8469 35.6346 46.9527 36.2731L51.4019 38.8419C52.5076 39.4803 52.8865 40.8943 52.248 42C51.6096 43.1058 50.1956 43.4846 49.0899 42.8462L44.6407 40.2774C43.5349 39.639 43.1561 38.225 43.7945 37.1192ZM18.8808 43.7945C19.9865 44.433 20.3654 45.8469 19.7269 46.9527L17.1581 51.4019C16.5197 52.5076 15.1057 52.8865 14 52.248C12.8942 51.6096 12.5154 50.1956 13.1538 49.0899L15.7226 44.6407C16.361 43.5349 17.775 43.1561 18.8808 43.7945ZM37.1192 43.7945C38.225 43.1561 39.639 43.5349 40.2774 44.6407L42.8462 49.0899C43.4846 50.1956 43.1058 51.6096 42 52.248C40.8943 52.8865 39.4803 52.5076 38.8419 51.4019L36.2731 46.9527C35.6346 45.8469 36.0135 44.433 37.1192 43.7945ZM28 46.2385C29.2768 46.2385 30.3119 47.2736 30.3119 48.5505V53.6881C30.3119 54.9649 29.2768 56 28 56C26.7232 56 25.6881 54.9649 25.6881 53.6881V48.5505C25.6881 47.2736 26.7232 46.2385 28 46.2385Z"
                            fill="#19191A"
                        />
                    </svg>
                </div>
                <div className={`${baseClass}__final--title`}>
                    {getTitle('10229', 'Processing your application')}...
                </div>
                <div className={`${baseClass}__final--description`}>
                    {getTitle('10230', 'This may take some time')}. {getTitle('10231', 'Please do not close this tab')}.
                </div>
            </div>
        )
    }

    if (isPostSendError && isFinalStep) {
        return (
            <div className={`${baseClass}__final`}>
                <div className={`${baseClass}__final--icon ${baseClass}__final--icon--error`}>
                    <svg width="51" height="52" viewBox="0 0 51 52" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M0 25.9997C0 11.9533 11.3869 0.566406 25.4333 0.566406C39.4798 0.566406 50.8667 11.9533 50.8667 25.9997C50.8667 40.0462 39.4798 51.4331 25.4333 51.4331C11.3869 51.4331 0 40.0462 0 25.9997ZM25.4334 30.4331C24.2736 30.4331 23.3334 29.4929 23.3334 28.3331V14.3331C23.3334 13.1733 24.2736 12.2331 25.4334 12.2331C26.5932 12.2331 27.5334 13.1733 27.5334 14.3331V28.3331C27.5334 29.4929 26.5932 30.4331 25.4334 30.4331ZM25.4334 37.6664C26.7221 37.6664 27.7667 36.6217 27.7667 35.3331C27.7667 34.0444 26.7221 32.9997 25.4334 32.9997C24.1447 32.9997 23.1001 34.0444 23.1001 35.3331C23.1001 36.6217 24.1447 37.6664 25.4334 37.6664Z"
                            fill="#B62626"
                        />
                    </svg>
                </div>
                <div className={`${baseClass}__final--title`}>{getTitle('5858', 'Something went wrong')}</div>
                <div className={`${baseClass}__final--description`}>
                    {getTitle('10237', 'We couldn’t submit your application')}. {getTitle('10238', 'Please try again')}.
                </div>
                <div className={`${baseClass}__final--buttons`}>
                    <ApplyV2Button
                        onClick={handlePostSendApplication}
                        className={`${baseClass}__final--buttons--btn ${baseClass}__final--buttons--btn-retry`}
                    >
                        {getTitle('6106', 'Try again')}
                    </ApplyV2Button>
                    <ApplyV2Button
                        variant={'outline'}
                        onClick={() => setIsFinalStep(false)}
                        className={`${baseClass}__final--buttons--btn ${baseClass}__final--buttons--btn-back`}
                    >
                        {getTitle('10235', 'Back to form')}
                    </ApplyV2Button>
                </div>
            </div>
        )
    }

    if (!isPostApplied && isFinalStep) {
        return (
            <div className={`${baseClass}__final`}>
                <div className={`${baseClass}__final--icon ${baseClass}__final--icon--error`}>
                    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M28 0C12.536 0 0 12.536 0 28C0 43.464 12.536 56 28 56C43.464 56 56 43.464 56 28C56 12.536 43.464 0 28 0ZM39.8815 23.2408C40.7999 22.3538 40.8252 20.8902 39.9382 19.9718C39.0511 19.0534 37.5875 19.028 36.6691 19.9151L24.9779 31.2077L19.331 25.7533C18.4126 24.8662 16.949 24.8916 16.062 25.81C15.1749 26.7284 15.2003 28.192 16.1186 29.079L23.3717 36.0849C24.2677 36.9503 25.6882 36.9503 26.5841 36.0849L39.8815 23.2408Z"
                            fill="#00853D"
                        />
                    </svg>
                </div>
                <div className={`${baseClass}__final--title`}>{getTitle('10236', 'Applied successfully')}!</div>
                <div className={`${baseClass}__final--description`}>
                    {getTitle(
                        '10240',
                        'Your application is now under review. Best of luck! Feel free to explore your profile or check out other job posts.'
                    )}
                </div>
                <CustomRadio
                    // name={'ApplyReview--considered'}
                    className={`${baseClass}__final--considered`}
                    title={getTitle('10243', 'Do you want to be considered for other job opportunities?')}
                    options={[
                        {
                            label: getTitle('10241', 'Yes, please'),
                            value: 1,
                        },
                        {
                            label: getTitle('10242', 'No, thanks'),
                            value: 0,
                        },
                    ]}
                    selectedValue={radioVal}
                    onChange={(v) => {
                        setRadioVal(v)
                    }}
                />
                <div className={`${baseClass}__final--buttons`}>
                    <ApplyV2Button
                        onClick={() => (window.location.href = '/profile')}
                        leftIcon={
                            <svg
                                width="17"
                                height="16"
                                viewBox="0 0 17 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M5.76657 4.80052C5.76657 3.29094 6.99032 2.06719 8.4999 2.06719C10.0095 2.06719 11.2332 3.29094 11.2332 4.80052C11.2332 6.3101 10.0095 7.53385 8.4999 7.53385C6.99032 7.53385 5.76657 6.3101 5.76657 4.80052ZM8.4999 0.867188C6.32758 0.867188 4.56657 2.6282 4.56657 4.80052C4.56657 6.32717 5.43631 7.65067 6.70733 8.30257C5.63762 8.58915 4.64991 9.13066 3.84529 9.89058C2.6041 11.0628 1.8999 12.6603 1.8999 14.3337C1.8999 14.6651 2.16853 14.9337 2.4999 14.9337C2.83127 14.9337 3.0999 14.6651 3.0999 14.3337C3.0999 13.0014 3.65999 11.7162 4.66924 10.763C5.67965 9.80872 7.05702 9.26706 8.4999 9.26706C9.94279 9.26706 11.3202 9.80872 12.3306 10.763C13.3398 11.7162 13.8999 13.0014 13.8999 14.3337C13.8999 14.6651 14.1685 14.9337 14.4999 14.9337C14.8313 14.9337 15.0999 14.6651 15.0999 14.3337C15.0999 12.6603 14.3957 11.0628 13.1545 9.89058C12.3499 9.13066 11.3622 8.58915 10.2925 8.30257C11.5635 7.65067 12.4332 6.32717 12.4332 4.80052C12.4332 2.6282 10.6722 0.867188 8.4999 0.867188Z"
                                    fill="white"
                                />
                            </svg>
                        }
                        className={`${baseClass}__final--buttons--btn ${baseClass}__final--buttons--btn-retry`}
                    >
                        {getTitle('6657', 'Go to profile')}
                    </ApplyV2Button>
                    <ApplyV2Button
                        variant={'outline'}
                        onClick={() => (window.location.href = '/profile/applications')}
                        className={`${baseClass}__final--buttons--btn ${baseClass}__final--buttons--btn-back`}
                        leftIcon={
                            <svg
                                width="17"
                                height="16"
                                viewBox="0 0 17 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M6.2575 2.25669C6.49181 2.02237 6.49181 1.64247 6.2575 1.40816C6.02318 1.17384 5.64329 1.17384 5.40897 1.40816L3.4999 3.31723L2.92417 2.74149C2.68985 2.50718 2.30995 2.50718 2.07564 2.74149C1.84132 2.97581 1.84132 3.3557 2.07564 3.59002L3.07564 4.59002C3.30995 4.82433 3.68985 4.82433 3.92417 4.59002L6.2575 2.25669ZM8.4999 2.39909C8.16853 2.39909 7.8999 2.66772 7.8999 2.99909C7.8999 3.33046 8.16853 3.59909 8.4999 3.59909H14.4999C14.8313 3.59909 15.0999 3.33046 15.0999 2.99909C15.0999 2.66772 14.8313 2.39909 14.4999 2.39909H8.4999ZM6.2575 6.40816C6.49181 6.64247 6.49181 7.02237 6.2575 7.25669L3.92417 9.59002C3.68985 9.82433 3.30995 9.82433 3.07564 9.59002L2.07564 8.59002C1.84132 8.3557 1.84132 7.97581 2.07564 7.74149C2.30995 7.50718 2.68985 7.50718 2.92417 7.74149L3.4999 8.31723L5.40897 6.40816C5.64329 6.17384 6.02318 6.17384 6.2575 6.40816ZM8.4999 7.39909C8.16853 7.39909 7.8999 7.66772 7.8999 7.99909C7.8999 8.33046 8.16853 8.59909 8.4999 8.59909H14.4999C14.8313 8.59909 15.0999 8.33046 15.0999 7.99909C15.0999 7.66772 14.8313 7.39909 14.4999 7.39909H8.4999ZM6.2575 11.4082C6.49181 11.6425 6.49181 12.0224 6.2575 12.2567L3.92417 14.59C3.68985 14.8243 3.30995 14.8243 3.07564 14.59L2.07564 13.59C1.84132 13.3557 1.84132 12.9758 2.07564 12.7415C2.30995 12.5072 2.68985 12.5072 2.92417 12.7415L3.4999 13.3172L5.40897 11.4082C5.64329 11.1738 6.02318 11.1738 6.2575 11.4082ZM8.4999 12.3991C8.16853 12.3991 7.8999 12.6677 7.8999 12.9991C7.8999 13.3305 8.16853 13.5991 8.4999 13.5991H14.4999C14.8313 13.5991 15.0999 13.3305 15.0999 12.9991C15.0999 12.6677 14.8313 12.3991 14.4999 12.3991H8.4999Z"
                                    fill="#19191A"
                                />
                            </svg>
                        }
                    >
                        {getTitle('10244', 'My applications')}
                    </ApplyV2Button>
                    <ApplyV2Button
                        variant={'transparent'}
                        leftIcon={<i className="fa-regular fa-xmark"></i>}
                        onClick={onCloseApply}
                        className={`${baseClass}__final--buttons--btn ${baseClass}__final--buttons--btn-back`}
                    >
                        {getTitle('10245', 'Close application')}
                    </ApplyV2Button>
                </div>
            </div>
        )
    }

    return (
        <div className={baseClass}>
            <ApplySkeleton isLoading={isLoading} style={{ minHeight: '100px' }}>
                <ApplyV2Accordion
                    title={getTitle('10202', 'Review your application')}
                    defaultExpanded={true}
                    className={`${baseClass}__accordion-main`}
                    icon={
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M3.26689 2.66576C3.26689 2.26075 3.59522 1.93242 4.00023 1.93242H8.06689V5.55464C8.06689 6.62239 8.93248 7.48798 10.0002 7.48798H12.7336V13.3324C12.7336 13.7374 12.4052 14.0658 12.0002 14.0658H4.00023C3.59522 14.0658 3.26689 13.7374 3.26689 13.3324V2.66576ZM9.26689 5.55464V2.253L12.5495 5.96336C12.6319 6.0565 12.6886 6.16835 12.7156 6.28798H10.0002C9.59522 6.28798 9.26689 5.95965 9.26689 5.55464ZM4.00023 0.732422C2.93248 0.732422 2.06689 1.598 2.06689 2.66576V13.3324C2.06689 14.4002 2.93248 15.2658 4.00023 15.2658H12.0002C13.068 15.2658 13.9336 14.4002 13.9336 13.3324V6.88798V6.44928C13.9336 5.97734 13.7609 5.52169 13.4482 5.16823L10.0917 1.37425C9.73059 0.966114 9.21183 0.732422 8.66689 0.732422H4.00023ZM5.06689 9.33242C5.06689 9.00105 5.33552 8.73242 5.66689 8.73242H10.3336C10.6649 8.73242 10.9336 9.00105 10.9336 9.33242C10.9336 9.66379 10.6649 9.93242 10.3336 9.93242H5.66689C5.33552 9.93242 5.06689 9.66379 5.06689 9.33242ZM5.06689 11.6658C5.06689 11.3344 5.33552 11.0658 5.66689 11.0658H10.3336C10.6649 11.0658 10.9336 11.3344 10.9336 11.6658C10.9336 11.9971 10.6649 12.2658 10.3336 12.2658H5.66689C5.33552 12.2658 5.06689 11.9971 5.06689 11.6658Z"
                                fill="#58585B"
                            />
                        </svg>
                    }
                >
                    {navItems.map((item) => (
                        <ApplyV2ReviewApplyStepItem
                            key={item.name}
                            title={getTitle(item.name)}
                            className={`${baseClass}__accordion-item`}
                            fieldType={item.name}
                        />
                    ))}
                    {/*<ApplyV2ReviewApplyStepItem className={`${baseClass}__accordion-item`} fieldType={'cv'} />*/}
                    {/*<ApplyV2ReviewApplyStepItem className={`${baseClass}__accordion-item`} fieldType={'profile'} />*/}
                </ApplyV2Accordion>
            </ApplySkeleton>
            <div className={`${baseClass}__attachs`}>
                <ApplySkeleton isLoading={isLoading}>
                    <div className={`${baseClass}__attachs__title`}>{getTitle('1111', 'Apply')}</div>
                </ApplySkeleton>
                {reviewApply?.fields?.attachments?.visible && (
                    <ApplySkeleton isLoading={isLoading}>
                        <div className={`${baseClass}__attachs__top`}>
                            <div className={`${baseClass}__cover--top`}>
                                <ApplySkeleton isLoading={isLoading}>
                                    <div className={`${baseClass}__cover--top-title`}>
                                        {getTitle('8166', 'Add attachments')}
                                        {reviewApply?.fields?.attachments?.required && '*'}
                                    </div>
                                </ApplySkeleton>
                                <ApplySkeleton isLoading={isLoading}>
                                    <div className={`${baseClass}__cover--top-count`}>
                                        {files.length}/{attachmentMaxFiles}
                                    </div>
                                </ApplySkeleton>
                            </div>
                            {files.length < attachmentMaxFiles && (
                                <FileUploaderV2
                                    className={`${baseClass}__attachs__uploader`}
                                    accept={['image/png, image/jpeg']}
                                    multiple={attachmentMaxFiles > 1}
                                    multipleLimit={attachmentMaxFiles - files.length}
                                    onChange={onSetAttachment}
                                >
                                    <div className={`${baseClass}__attachs__uploader-content`}>
                                        <div className={`${baseClass}__attachs__uploader-content-icon`}>
                                            <svg
                                                width="64"
                                                height="64"
                                                viewBox="0 0 64 64"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M32.0001 57.5995C47.3163 57.5995 59.7334 45.2068 59.7334 29.8661C59.7334 14.5255 47.2616 2.13281 32.0001 2.13281C16.6838 2.13281 4.26672 14.5255 4.26672 29.8661C4.26672 45.2068 16.6838 57.5995 32.0001 57.5995Z"
                                                    fill="#F6F6F6"
                                                    stroke="#C3C3C3"
                                                    strokeMiterlimit="10"
                                                />
                                                <path
                                                    d="M47.5901 48.5515H55.5052C56.4994 48.5515 57.3515 47.6998 57.3515 46.7061V9.7985C57.3515 8.80484 56.4994 7.95312 55.5052 7.95312H47.8358"
                                                    fill="#EEEEEE"
                                                />
                                                <path
                                                    d="M47.5901 48.5515H55.5052C56.4994 48.5515 57.3515 47.6998 57.3515 46.7061V9.7985C57.3515 8.80484 56.4994 7.95312 55.5052 7.95312H47.8358L47.5901 48.5515Z"
                                                    stroke="#C3C3C3"
                                                    strokeMiterlimit="10"
                                                />
                                                <path
                                                    d="M16.1641 48.5515H8.49477C7.50059 48.5515 6.64844 47.6998 6.64844 46.7061V9.7985C6.64844 8.80484 7.50059 7.95312 8.49477 7.95312H16.0062"
                                                    fill="#EEEEEE"
                                                />
                                                <path
                                                    d="M16.1641 48.5515H8.49477C7.50059 48.5515 6.64844 47.6998 6.64844 46.7061V9.7985C6.64844 8.80484 7.50059 7.95312 8.49477 7.95312H16.0062L16.1641 48.5515Z"
                                                    stroke="#C3C3C3"
                                                    strokeMiterlimit="10"
                                                />
                                                <path
                                                    d="M11.984 15.334H15.8187"
                                                    stroke="#C3C3C3"
                                                    strokeMiterlimit="10"
                                                    strokeLinecap="round"
                                                />
                                                <path
                                                    d="M11.984 20.3027H15.8187"
                                                    stroke="#C3C3C3"
                                                    strokeMiterlimit="10"
                                                    strokeLinecap="round"
                                                />
                                                <path
                                                    d="M11.984 25.4121H15.8187"
                                                    stroke="#C3C3C3"
                                                    strokeMiterlimit="10"
                                                    strokeLinecap="round"
                                                />
                                                <path
                                                    d="M11.984 30.5234H15.8187"
                                                    stroke="#C3C3C3"
                                                    strokeMiterlimit="10"
                                                    strokeLinecap="round"
                                                />
                                                <path
                                                    d="M11.984 40.459H15.8187"
                                                    stroke="#C3C3C3"
                                                    strokeMiterlimit="10"
                                                    strokeLinecap="round"
                                                />
                                                <path
                                                    d="M51.5285 15.334H47.8358"
                                                    stroke="#C3C3C3"
                                                    strokeMiterlimit="10"
                                                />
                                                <path
                                                    d="M51.5285 20.3027H47.8358"
                                                    stroke="#C3C3C3"
                                                    strokeMiterlimit="10"
                                                    strokeLinecap="round"
                                                />
                                                <path
                                                    d="M51.5285 25.4121H47.8358"
                                                    stroke="#C3C3C3"
                                                    strokeMiterlimit="10"
                                                    strokeLinecap="round"
                                                />
                                                <path
                                                    d="M51.5285 30.5234H47.8358"
                                                    stroke="#C3C3C3"
                                                    strokeMiterlimit="10"
                                                    strokeLinecap="round"
                                                />
                                                <path
                                                    d="M51.5285 40.459H47.8358"
                                                    stroke="#C3C3C3"
                                                    strokeMiterlimit="10"
                                                    strokeLinecap="round"
                                                />
                                                <path
                                                    d="M45.9895 51.6736H17.8684C16.8742 51.6736 16.0221 50.8219 16.0221 49.8282V6.95866C16.0221 5.96499 16.8742 5.11328 17.8684 5.11328H45.9895C46.9836 5.11328 47.8358 5.96499 47.8358 6.95866V49.8282C47.8358 50.8219 46.9836 51.6736 45.9895 51.6736Z"
                                                    fill="white"
                                                    stroke="#C9C9C9"
                                                    strokeMiterlimit="10"
                                                />
                                                <path
                                                    d="M42.5809 19.0015H22.9814C22.5553 19.0015 22.2712 18.7442 22.2712 18.3583V16.6861C22.2712 16.3002 22.5553 16.043 22.9814 16.043H42.5809C43.007 16.043 43.291 16.3002 43.291 16.6861V18.3583C43.291 18.6156 43.007 19.0015 42.5809 19.0015Z"
                                                    fill="#EEEEEE"
                                                    stroke="#C3C3C3"
                                                    strokeMiterlimit="10"
                                                />
                                                <path
                                                    d="M42.5809 26.6675H22.9814C22.5553 26.6675 22.2712 26.4102 22.2712 26.0243V24.3521C22.2712 23.9662 22.5553 23.709 22.9814 23.709H42.5809C43.007 23.709 43.291 23.9662 43.291 24.3521V26.0243C43.291 26.4102 43.007 26.6675 42.5809 26.6675Z"
                                                    fill="#EEEEEE"
                                                    stroke="#C3C3C3"
                                                    strokeMiterlimit="10"
                                                />
                                                <path
                                                    d="M42.5809 34.4741H22.9814C22.5553 34.4741 22.2712 34.2169 22.2712 33.831V32.1588C22.2712 31.7729 22.5553 31.5156 22.9814 31.5156H42.5809C43.007 31.5156 43.291 31.7729 43.291 32.1588V33.831C43.291 34.2169 43.007 34.4741 42.5809 34.4741Z"
                                                    fill="#EEEEEE"
                                                    stroke="#C3C3C3"
                                                    strokeMiterlimit="10"
                                                />
                                                <path
                                                    d="M42.581 42.4234H34.7696C34.3435 42.4234 34.0594 42.1661 34.0594 41.7802V40.108C34.0594 39.7221 34.3435 39.4648 34.7696 39.4648H42.581C43.0071 39.4648 43.2911 39.7221 43.2911 40.108V41.7802C43.2911 42.1661 43.0071 42.4234 42.581 42.4234Z"
                                                    fill="#EEEEEE"
                                                    stroke="#C3C3C3"
                                                    strokeMiterlimit="10"
                                                />
                                                <path
                                                    d="M41.928 53.7995C41.7212 54.6767 41.4111 55.6055 41.0493 56.3795C40.0672 58.2888 38.5165 59.7852 36.604 60.7656C34.6399 61.746 32.3139 62.1588 29.9879 61.6428C24.4572 60.456 20.9424 55.1411 22.0795 49.6714C23.2167 44.2017 28.5406 40.6412 34.0196 41.828C35.9838 42.2408 37.6895 43.2212 39.1368 44.5629C41.6178 46.9881 42.5999 50.4454 41.928 53.7995Z"
                                                    fill="#C3C3C3"
                                                    stroke="#959595"
                                                    strokeMiterlimit="10"
                                                />
                                                <path
                                                    d="M31.9996 47.1562C32.8108 47.1562 33.3784 47.8114 33.3785 48.5332V50.3555H35.2047C35.9257 50.3555 36.5834 50.9206 36.5836 51.7324C36.5836 52.5444 35.9258 53.1104 35.2047 53.1104H33.3785V54.9316C33.3785 55.7436 32.7207 56.3096 31.9996 56.3096C31.1886 56.3094 30.6217 55.6535 30.6217 54.9316V53.1104H28.7955C28.4177 53.1104 28.0668 52.9758 27.8092 52.7188C27.5515 52.4615 27.4166 52.1105 27.4166 51.7324C27.4168 50.9206 28.0745 50.3555 28.7955 50.3555H30.6217V48.5332C30.6218 47.7215 31.2788 47.1565 31.9996 47.1562Z"
                                                    fill="white"
                                                    stroke="#959595"
                                                />
                                            </svg>
                                        </div>
                                        <div className={`${baseClass}__attachs__uploader-content-text`}>
                                            {attachmentDescription}
                                        </div>
                                    </div>
                                </FileUploaderV2>
                            )}
                        </div>
                    </ApplySkeleton>
                )}
                {files.length > 0 && (
                    <ApplySkeleton isLoading={isLoading}>
                        <FileUploaderV2List
                            files={files}
                            isShowDelete={true}
                            isShowParseInfo={false}
                            onDelete={onDeleteAttachment}
                        ></FileUploaderV2List>
                    </ApplySkeleton>
                )}
            </div>
            {reviewApply?.fields?.applicationText?.visible && (
                <div className={`${baseClass}__cover`}>
                    <div className={`${baseClass}__cover--top`}>
                        <ApplySkeleton isLoading={isLoading}>
                            <div className={`${baseClass}__cover--top-title`}>
                                {getTitle('10227', 'Cover letter')}
                                {reviewApply?.fields?.applicationText?.required && '*'}
                            </div>
                        </ApplySkeleton>
                        <ApplySkeleton isLoading={isLoading}>
                            <div className={`${baseClass}__cover--top-count`}>
                                {coverTextCount}/{coverTextLimit}
                            </div>
                        </ApplySkeleton>
                    </div>
                    <ApplySkeleton isLoading={isLoading}>
                        <ApplyV2Textarea
                            className={`${baseClass}__cover--textarea`}
                            value={coverText}
                            placeholder={getTitle('7606', 'Description text')}
                            isLoading={isPostApplicationTextLoading}
                            isError={isPostApplicationTextError}
                            onChange={(v) => {
                                // console.log('coverText', v)
                                handlePostApplicationText(v)
                            }}
                        ></ApplyV2Textarea>
                    </ApplySkeleton>
                    {coverTextError && (
                        <div className={`${baseClass}__cover--textarea--error`}>
                            <div className={`${baseClass}__cover--textarea--error-icon`}>
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clip-path="url(#clip0_4667_34531)">
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M8.00006 1.93242C4.64954 1.93242 1.9334 4.64856 1.9334 7.99909C1.9334 11.3496 4.64954 14.0658 8.00006 14.0658C11.3506 14.0658 14.0667 11.3496 14.0667 7.99909C14.0667 4.64856 11.3506 1.93242 8.00006 1.93242ZM0.733398 7.99909C0.733398 3.98582 3.9868 0.732422 8.00006 0.732422C12.0133 0.732422 15.2667 3.98582 15.2667 7.99909C15.2667 12.0124 12.0133 15.2658 8.00006 15.2658C3.9868 15.2658 0.733398 12.0124 0.733398 7.99909ZM8.00006 9.26575C7.66869 9.26575 7.40007 8.99713 7.40007 8.66576V4.66576C7.40007 4.33438 7.66869 4.06575 8.00006 4.06575C8.33144 4.06575 8.60006 4.33438 8.60006 4.66576V8.66576C8.60006 8.99713 8.33144 9.26575 8.00006 9.26575ZM8.00006 11.3324C8.36826 11.3324 8.66673 11.0339 8.66673 10.6658C8.66673 10.2976 8.36826 9.99909 8.00006 9.99909C7.63188 9.99909 7.3334 10.2976 7.3334 10.6658C7.3334 11.0339 7.63188 11.3324 8.00006 11.3324Z"
                                            fill="#B62626"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_4667_34531">
                                            <rect width="16" height="16" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </div>
                            <div className={`${baseClass}__cover--textarea--error-message`}> {coverTextError}</div>
                        </div>
                    )}
                </div>
            )}
            <div className={`${baseClass}__footer`}>
                <ApplySkeleton isLoading={isLoading} style={{ flexGrow: 1 }}>
                    <div className={`${baseClass}__footer--text`}>
                        By clicking "Submit application" you agree to our{' '}
                        <a href={'/terms-of-service'} target="_blank" rel="noreferrer">
                            Terms of Service
                        </a>{' '}
                        and{' '}
                        <a href={'/privacy-policy'} target="_blank" rel="noreferrer">
                            Privacy Policy
                        </a>
                    </div>
                </ApplySkeleton>
                <ApplySkeleton isLoading={isLoading}>
                    <ApplyV2Button
                        className={`${baseClass}__footer--submit`}
                        onClick={handlePostSendApplication}
                        isDisabled={!isValidForm}
                    >
                        {getTitle('1111', 'Apply')}
                    </ApplyV2Button>
                </ApplySkeleton>
            </div>
        </div>
    )
}

export default ApplyV2ReviewApplyStep
